import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ItemMilestone } from '@/types/item';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { useAppSettings } from '@/hooks/useAppSettings';
import { hexToRgba, resolveHighlightStyle, HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS } from '@/utils/highlightStyle';

// Helper function to get local date string
const ymd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface MilestoneMarkerProps {
  milestone: ItemMilestone;
  dateColumns: Date[];
  dayWidth?: number;
  staticMilestoneTypes?: any[]; // Static milestone types for print context
  highlighted?: boolean;
  onRequestDependency?: (milestoneId: string) => void;
}

export default function MilestoneMarker({ 
  milestone, 
  dateColumns, 
  dayWidth = 20,
  staticMilestoneTypes,
  highlighted = false,
  onRequestDependency,
}: MilestoneMarkerProps) {

  const { data: milestoneTypes = [] } = useMilestoneTypes();
  const { getSetting } = useAppSettings();
  const highlightStyle = resolveHighlightStyle(getSetting(HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS));

  // Use static data if available (print context), otherwise use hook data
  const effectiveTypes = staticMilestoneTypes || milestoneTypes;
  
  const milestoneTypeColor = useMemo(() => {
    const type = effectiveTypes.find(t => t.name === milestone.milestone_type);
    return type?.color || '#64748b';
  }, [milestone.milestone_type, effectiveTypes]);

  const markerPosition = useMemo(() => {
    const startIndex = dateColumns.findIndex(date => ymd(date) === milestone.start_date);
    
    if (startIndex === -1) {
      return null; // Milestone start is outside visible range
    }
    
    const left = startIndex * dayWidth;
    
    return { left };
  }, [milestone.start_date, dateColumns, dayWidth]);

  if (!markerPosition) {
    return null;
  }

  const markerElement = (
    <div
      className={cn(
        "absolute rounded-full border border-background shadow-sm cursor-pointer"
      )}
      style={{
        left: `${markerPosition.left + dayWidth - 10}px`,
        top: '4px',
        width: '8px',
        height: '8px',
        backgroundColor: highlighted ? highlightStyle.fill_color : milestoneTypeColor,
        boxShadow: highlighted ? `0 0 0 2px ${hexToRgba(highlightStyle.fill_color, 0.4)}` : undefined,
        opacity: 1,
        zIndex: 30
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onRequestDependency?.(milestone.id);
      }}
    />
  );


  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {markerElement}
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-medium">{milestone.name}</div>
          <div className="text-muted-foreground text-[10px]">
            {milestone.start_date}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
